import React, { useState } from "react"
import { Alert } from "reactstrap"
import { Link } from "react-router-dom"
import { daysLabel, placeLabel } from "./plannerLabels"
import { FIXABLE, fixLabel, outcomeLine, problemLabel, publicationEmailLine, resendable, settling } from "./publishingLabels"
import usePublicationEmails from "./usePublicationEmails"
import PublicationResendDialog from "./PublicationResendDialog"

// A publish and its result (ticket 18; integrator default: progress per proposal is its outcome): each proposal of the batch published as
// its event, refused (nothing created; "Fix and publish" takes it back to the review) or failed (Retry), and those left out because
// incomplete; then each organizer's email with how it goes, sent again when it failed (E1p pinned decisions). `run.items` are the
// proposals sent, `run.skipped` those left out; `run.result` is the command's answer once it has one.
// `onRetry(items)` publishes those again: a failed one alone, or the whole batch after a publish that was refused or not confirmed (its
// proposals published meanwhile are no longer Confirmed, so they are not sent again).
export default function PublishResult({ run, commands, onFix, onRetry, onBack, onShowCalendar }) {
  const outcomes = run.result?.proposals || []
  const outcomeOf = item => outcomes.find(outcome => outcome.id === item.id)
  const done = Boolean(run.result || run.error)
  const count = outcomes.filter(outcome => outcome.outcome === "published").length
  const title = done ? `Published ${count} of ${run.items.length + run.skipped.length}` : "Publishing"
  const { emails, polling, again } = usePublicationEmails(run.result)
  const [resending, setResending] = useState(null)
  const resendingEmail = emails.find(email => email.publicationId === resending)
  const nameOf = organizerId => run.items.find(item => item.organizer?.id === organizerId)?.organizer.name || "The organizer"
  // One proposal of the publish: its number, what it is, what happened to it and what can be done next.
  const entry = (item, status, action) => <li key={item.id} aria-label={item.number}>
    <b>{item.number}</b>
    <span className="ep-stack">
      <span className="ep-meta">{`${item.name || "No name yet"} · ${daysLabel(item.days)} · ${placeLabel(item)}`}</span>
      {status}
    </span>
    {action}
  </li>

  return <section className="ep-card ep-stack ep-gap ep-publish-run" aria-label={title}>
    <h2>{done ? title : `Publishing ${run.items.length === 1 ? "1 proposal" : `${run.items.length} proposals`}…`}</h2>
    {run.error && <Alert color="danger" className="mb-0 ep-banner-row"><span>{run.error}</span>
      <button type="button" className="btn btn-light btn-sm" onClick={() => onRetry(run.items)}>Try again</button></Alert>}
    <ul className="ep-day-list ep-publish-outcomes">
      {run.items.map(item => {
        const outcome = outcomeOf(item)
        return entry(item, <>
          {!done && <span>Publishing…</span>}
          {outcome && <span className={outcome.outcome === "published" ? "" : "ep-error-text"}>{outcomeLine(outcome)}</span>}
        </>, <>
          {outcome?.outcome === "published" && <Link className="btn btn-light btn-sm" to={`/events/${outcome.eventId}`}>{`Open event #${outcome.eventNumber}`}</Link>}
          {outcome?.outcome === "refused" && FIXABLE.includes(outcome.message)
            && <button type="button" className="btn btn-light btn-sm" onClick={() => onFix(item)}>{fixLabel(outcome.message)}</button>}
          {outcome?.outcome === "failed" && <button type="button" className="btn btn-light btn-sm" onClick={() => onRetry([item])}>Retry</button>}
        </>)
      })}
      {run.skipped.map(item => entry(item, <span className="ep-error-text">{`Not published: incomplete (${item.problems.map(problem => problemLabel(problem, item.competitions)).join("; ")}).`}</span>,
        <button type="button" className="btn btn-light btn-sm" onClick={() => onFix(item)}>Fix and publish</button>))}
    </ul>
    {run.result && <section className="ep-stack" aria-label="Emails to the organizers">
      <h3>Emails to the organizers</h3>
      {emails.length ? <ul className="ep-day-list">
        {emails.map(email => <li key={email.organizerId} aria-label={nameOf(email.organizerId)}>
          <b>{nameOf(email.organizerId)}</b>
          <span className={resendable(email) ? "ep-error-text" : undefined}>{publicationEmailLine(email)}</span>
          {resendable(email) && <button type="button" className="btn btn-light btn-sm" onClick={() => setResending(email.publicationId)}>Send again…</button>}
        </li>)}
      </ul> : <span className="ep-meta">No organizer was emailed.</span>}
      {!polling && emails.some(settling) && <button type="button" className="btn btn-link btn-sm align-self-start p-0" onClick={again}>Check again</button>}
    </section>}
    {done && <div className="ep-panel-actions">
      <span className="ep-meta ep-spacer">{count ? `${count === 1 ? "1 event was" : `${count} events were`} created with their competitions, without fees, and their logistics sheets. Set the fees on each event before its registrations open.`
        : "No event was created."}</span>
      <button type="button" className="btn btn-light" onClick={onBack}>Back to Ready to publish</button>
      <button type="button" className="btn btn-primary" onClick={onShowCalendar}>Show in calendar</button>
    </div>}
    {resendingEmail && <PublicationResendDialog email={resendingEmail} name={nameOf(resendingEmail.organizerId)} commands={commands}
      onClose={() => setResending(null)} onSent={again} />}
  </section>
}
